import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { BarChart3, DollarSign, Package, Users, Truck, Megaphone, Layout, TrendingUp, Mail, Instagram, MessageSquare } from 'lucide-react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#22d3ee', '#22c55e', '#eab308', '#ef4444', '#64748b', '#8b5cf6', '#ec4899', '#f97316'];

export default function Analytics() {
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [salesAnalytics, setSalesAnalytics] = useState(null);
  const [customerAnalytics, setCustomerAnalytics] = useState(null);
  const [inventoryAnalytics, setInventoryAnalytics] = useState(null);
  const [staffingAnalytics, setStaffingAnalytics] = useState(null);
  const [marketingAnalytics, setMarketingAnalytics] = useState(null);
  const [boothAnalytics, setBoothAnalytics] = useState(null);
  const [growthAnalytics, setGrowthAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('financial');

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [analyticsRes, eventsRes, salesRes, customerRes, inventoryRes, staffingRes, marketingRes, boothRes, growthRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/analytics/summary`, { headers }),
        axios.get(`${BACKEND_URL}/api/events`, { headers }),
        axios.get(`${BACKEND_URL}/api/product-sales/analytics`, { headers }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/customer-analytics`, { headers }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/inventory-analytics`, { headers }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/staffing-analytics`, { headers }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/marketing-analytics`, { headers }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/booth-analytics`, { headers }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/growth-analytics`, { headers }).catch(() => ({ data: null })),
      ]);

      setStats(analyticsRes.data);
      setEvents(eventsRes.data);
      setSalesAnalytics(salesRes.data);
      setCustomerAnalytics(customerRes.data);
      setInventoryAnalytics(inventoryRes.data);
      setStaffingAnalytics(staffingRes.data);
      setMarketingAnalytics(marketingRes.data);
      setBoothAnalytics(boothRes.data);
      setGrowthAnalytics(growthRes.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">Loading analytics...</div></div>;
  }

  const statusData = Object.entries(stats?.status_counts || {}).map(([key, value]) => ({
    name: key.replace('_', ' ').toUpperCase(),
    value,
  }));

  const expenseData = Object.entries(stats?.expense_by_category || {}).map(([key, value]) => ({
    category: key.replace('_', ' ').toUpperCase(),
    amount: value,
  }));

  const eventProfitData = events
    .map((e) => ({
      name: e.name.length > 20 ? e.name.substring(0, 20) + '...' : e.name,
      profit: (e.total_revenue || 0) - (e.total_expenses || 0) - (e.booth_cost || 0),
    }))
    .sort((a, b) => b.profit - a.profit)
    .slice(0, 10);

  const showQualityData = events.filter(e => e.buyer_intent_rating || e.foot_traffic_rating).map(e => ({
    name: e.name.length > 15 ? e.name.substring(0, 15) + '...' : e.name,
    buyerIntent: e.buyer_intent_rating || 0,
    footTraffic: e.foot_traffic_rating || 0,
  }));

  const salesByType = salesAnalytics?.by_product_type 
    ? Object.entries(salesAnalytics.by_product_type).map(([type, data]) => ({ name: type, quantity: data.quantity, revenue: data.revenue }))
    : [];

  const revenueByEvent = events.filter(e => e.total_revenue > 0).map(e => ({
    name: e.name.length > 12 ? e.name.substring(0, 12) + '...' : e.name,
    revenue: e.total_revenue || 0,
  })).sort((a, b) => b.revenue - a.revenue).slice(0, 8);

  const StatCard = ({ title, value, subtitle, color = 'text-foreground' }) => (
    <Card className="bg-card border-border/50">
      <CardContent className="p-3 sm:p-4">
        <p className="text-[10px] sm:text-xs text-muted-foreground uppercase">{title}</p>
        <p className={`text-lg sm:text-2xl font-bold font-mono ${color}`}>{value}</p>
        {subtitle && <p className="text-[10px] text-muted-foreground">{subtitle}</p>}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4 sm:space-y-6 pb-20">
      <div>
        <h1 className="text-2xl sm:text-4xl font-bold font-heading tracking-tight" data-testid="analytics-title">Analytics</h1>
        <p className="text-muted-foreground mt-1 text-sm sm:text-base">Comprehensive business insights and metrics</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full grid grid-cols-4 sm:grid-cols-8 sm:w-auto overflow-x-auto">
          <TabsTrigger value="financial" className="flex items-center gap-1 text-xs sm:text-sm"><DollarSign className="h-3 w-3 sm:h-4 sm:w-4" /><span className="hidden sm:inline">Financial</span></TabsTrigger>
          <TabsTrigger value="sales" className="flex items-center gap-1 text-xs sm:text-sm"><Package className="h-3 w-3 sm:h-4 sm:w-4" /><span className="hidden sm:inline">Sales</span></TabsTrigger>
          <TabsTrigger value="showQuality" className="flex items-center gap-1 text-xs sm:text-sm"><TrendingUp className="h-3 w-3 sm:h-4 sm:w-4" /><span className="hidden sm:inline">Shows</span></TabsTrigger>
          <TabsTrigger value="customer" className="flex items-center gap-1 text-xs sm:text-sm"><Users className="h-3 w-3 sm:h-4 sm:w-4" /><span className="hidden sm:inline">Customer</span></TabsTrigger>
          <TabsTrigger value="inventory" className="flex items-center gap-1 text-xs sm:text-sm"><Truck className="h-3 w-3 sm:h-4 sm:w-4" /><span className="hidden sm:inline">Inventory</span></TabsTrigger>
          <TabsTrigger value="marketing" className="flex items-center gap-1 text-xs sm:text-sm"><Megaphone className="h-3 w-3 sm:h-4 sm:w-4" /><span className="hidden sm:inline">Marketing</span></TabsTrigger>
          <TabsTrigger value="booth" className="flex items-center gap-1 text-xs sm:text-sm"><Layout className="h-3 w-3 sm:h-4 sm:w-4" /><span className="hidden sm:inline">Booth</span></TabsTrigger>
          <TabsTrigger value="growth" className="flex items-center gap-1 text-xs sm:text-sm"><BarChart3 className="h-3 w-3 sm:h-4 sm:w-4" /><span className="hidden sm:inline">Growth</span></TabsTrigger>
        </TabsList>

        {/* Financial Tab */}
        <TabsContent value="financial" className="mt-4 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
            <StatCard title="Revenue" value={formatCurrency(stats?.total_revenue)} color="text-green-400" />
            <StatCard title="Expenses" value={formatCurrency(stats?.total_expenses)} color="text-red-400" />
            <StatCard title="Booth Costs" value={formatCurrency(stats?.total_booth_costs)} color="text-orange-400" />
            <StatCard title="Net Profit" value={formatCurrency(stats?.net_profit)} color={stats?.net_profit >= 0 ? 'text-green-400' : 'text-red-400'} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="bg-card border-border/50">
              <CardHeader><CardTitle className="text-base">Expenses by Category</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={expenseData}><CartesianGrid strokeDasharray="3 3" stroke="#27272a" /><XAxis dataKey="category" stroke="#a1a1aa" tick={{ fontSize: 10 }} /><YAxis stroke="#a1a1aa" /><Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }} /><Bar dataKey="amount" fill="#22d3ee" /></BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card className="bg-card border-border/50">
              <CardHeader><CardTitle className="text-base">Event Status</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart><Pie data={statusData} cx="50%" cy="50%" outerRadius={80} fill="#8884d8" dataKey="value" label={({ name, value }) => `${name}: ${value}`}>{statusData.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}</Pie><Tooltip contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }} /></PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
          <Card className="bg-card border-border/50">
            <CardHeader><CardTitle className="text-base">Top Events by Profit/Loss</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={eventProfitData} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#27272a" /><XAxis type="number" stroke="#a1a1aa" /><YAxis dataKey="name" type="category" stroke="#a1a1aa" width={120} tick={{ fontSize: 11 }} /><Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }} /><Bar dataKey="profit" fill="#22d3ee" /></BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sales Tab */}
        <TabsContent value="sales" className="mt-4 space-y-4">
          {salesAnalytics ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
                <StatCard title="Total Units" value={salesAnalytics.total_quantity} />
                <StatCard title="Sales Revenue" value={formatCurrency(salesAnalytics.total_revenue)} color="text-green-400" />
                <StatCard title="COGS" value={formatCurrency(salesAnalytics.total_cogs)} color="text-red-400" />
                <StatCard title="Gross Profit" value={formatCurrency(salesAnalytics.gross_profit)} color={salesAnalytics.gross_profit >= 0 ? 'text-green-400' : 'text-red-400'} />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="bg-card border-border/50">
                  <CardHeader><CardTitle className="text-base">Sales by Product Type</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={salesByType}><CartesianGrid strokeDasharray="3 3" stroke="#27272a" /><XAxis dataKey="name" stroke="#a1a1aa" tick={{ fontSize: 10 }} /><YAxis stroke="#a1a1aa" /><Tooltip contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }} /><Bar dataKey="quantity" fill="#22d3ee" name="Units Sold" /></BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border/50">
                  <CardHeader><CardTitle className="text-base">Top Sellers</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {salesAnalytics.top_sellers?.map((item, idx) => (
                        <div key={item.name} className="flex items-center justify-between p-2 bg-secondary/30 rounded-lg">
                          <div className="flex items-center gap-2">
                            <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${idx < 3 ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}>{idx + 1}</span>
                            <span className="text-sm truncate max-w-[150px]">{item.name}</span>
                          </div>
                          <div className="text-right">
                            <p className="font-mono text-sm text-green-400">{formatCurrency(item.revenue)}</p>
                            <p className="text-xs text-muted-foreground">{item.quantity} units</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          ) : (
            <Card className="p-8 text-center bg-card border-border/50"><Package className="h-12 w-12 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">No sales data recorded yet</p></Card>
          )}
        </TabsContent>

        {/* Shows Tab */}
        <TabsContent value="showQuality" className="mt-4 space-y-4">
          {showQualityData.length > 0 ? (
            <>
              <Card className="bg-card border-border/50">
                <CardHeader><CardTitle className="text-base">Buyer Intent vs Foot Traffic</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={showQualityData}><CartesianGrid strokeDasharray="3 3" stroke="#27272a" /><XAxis dataKey="name" stroke="#a1a1aa" tick={{ fontSize: 10 }} /><YAxis stroke="#a1a1aa" domain={[0, 10]} /><Tooltip contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }} /><Legend /><Bar dataKey="buyerIntent" fill="#22d3ee" name="Buyer Intent" /><Bar dataKey="footTraffic" fill="#22c55e" name="Foot Traffic" /></BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="bg-card border-border/50">
                  <CardHeader><CardTitle className="text-base">Best by Buyer Intent</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {showQualityData.sort((a, b) => b.buyerIntent - a.buyerIntent).slice(0, 5).map((show) => (
                        <div key={show.name} className="flex items-center justify-between p-2 bg-secondary/30 rounded-lg">
                          <span className="text-sm">{show.name}</span>
                          <span className={`font-bold ${show.buyerIntent >= 7 ? 'text-green-400' : show.buyerIntent >= 4 ? 'text-yellow-400' : 'text-red-400'}`}>{show.buyerIntent}/10</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border/50">
                  <CardHeader><CardTitle className="text-base">Best by Foot Traffic</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {showQualityData.sort((a, b) => b.footTraffic - a.footTraffic).slice(0, 5).map((show) => (
                        <div key={show.name} className="flex items-center justify-between p-2 bg-secondary/30 rounded-lg">
                          <span className="text-sm">{show.name}</span>
                          <span className={`font-bold ${show.footTraffic >= 7 ? 'text-green-400' : show.footTraffic >= 4 ? 'text-yellow-400' : 'text-red-400'}`}>{show.footTraffic}/10</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          ) : (
            <Card className="p-8 text-center bg-card border-border/50"><TrendingUp className="h-12 w-12 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground mb-2">No show quality data</p><p className="text-sm text-muted-foreground">Add metrics in each event&apos;s Show Quality tab</p></Card>
          )}
        </TabsContent>

        {/* Customer Tab */}
        <TabsContent value="customer" className="mt-4 space-y-4">
          {customerAnalytics && customerAnalytics.total_contacts > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
                <StatCard title="Emails Collected" value={customerAnalytics.total_emails_collected} subtitle="Newsletter signups" />
                <StatCard title="SMS Collected" value={customerAnalytics.total_sms_collected} subtitle="Text list" />
                <StatCard title="IG Follows" value={customerAnalytics.total_instagram_follows} subtitle="New followers" />
                <StatCard title="QR Scans" value={customerAnalytics.total_qr_scans} subtitle="Link clicks" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4">
                <StatCard title="Total Transactions" value={customerAnalytics.total_transactions} />
                <StatCard title="Avg Order Value" value={formatCurrency(customerAnalytics.avg_order_value)} color="text-primary" />
                <StatCard title="Items/Transaction" value={customerAnalytics.avg_items_per_transaction?.toFixed(1)} />
              </div>
              {customerAnalytics.requested_items?.length > 0 && (
                <Card className="bg-card border-border/50">
                  <CardHeader><CardTitle className="text-base">Customer Requests</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {customerAnalytics.requested_items.map((item, idx) => (
                        <p key={idx} className="text-sm text-muted-foreground p-2 bg-secondary/30 rounded">{item}</p>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card className="p-8 text-center bg-card border-border/50">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground mb-2">No customer data recorded yet</p>
              <p className="text-sm text-muted-foreground">Track emails, SMS, and social follows per event</p>
            </Card>
          )}
        </TabsContent>

        {/* Inventory Tab */}
        <TabsContent value="inventory" className="mt-4 space-y-4">
          {inventoryAnalytics && inventoryAnalytics.total_events_tracked > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
                <StatCard title="Events Tracked" value={inventoryAnalytics.total_events_tracked} />
                <StatCard title="Avg Bins/Event" value={inventoryAnalytics.avg_bins_per_event} />
                <StatCard title="Avg Load-In" value={`${inventoryAnalytics.avg_load_in_minutes} min`} />
                <StatCard title="Avg Setup" value={`${inventoryAnalytics.avg_setup_minutes} min`} />
              </div>
              {inventoryAnalytics.total_shrinkage_incidents > 0 && (
                <Card className="bg-red-500/10 border-red-500/30">
                  <CardHeader><CardTitle className="text-base text-red-400">Shrinkage Incidents</CardTitle></CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-red-400 mb-2">{inventoryAnalytics.total_shrinkage_incidents}</p>
                    <div className="space-y-1">
                      {inventoryAnalytics.shrinkage_events?.slice(0, 5).map((s, idx) => (
                        <p key={idx} className="text-sm text-muted-foreground">{s.product}: {s.shrinkage} missing</p>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card className="p-8 text-center bg-card border-border/50">
              <Truck className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground mb-2">No logistics data recorded yet</p>
              <p className="text-sm text-muted-foreground">Track load times, bins, and shrinkage per event</p>
            </Card>
          )}
        </TabsContent>

        {/* Marketing Tab */}
        <TabsContent value="marketing" className="mt-4 space-y-4">
          {marketingAnalytics && marketingAnalytics.total_events_tracked > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
                <StatCard title="Pre-Show Posts" value={marketingAnalytics.total_pre_show_posts} />
                <StatCard title="Reels Views" value={marketingAnalytics.total_reels_views?.toLocaleString()} />
                <StatCard title="Link Clicks" value={marketingAnalytics.total_link_clicks} />
                <StatCard title="QR Scans" value={marketingAnalytics.total_qr_scans} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4">
                <StatCard title="Discount Uses" value={marketingAnalytics.total_discount_uses} />
                <StatCard title="Post-Show Spike" value={formatCurrency(marketingAnalytics.total_post_show_sales_spike)} color="text-green-400" subtitle="24-72hr sales" />
                <StatCard title="Avg Views/Post" value={marketingAnalytics.avg_reels_views_per_post?.toLocaleString()} />
              </div>
            </>
          ) : (
            <Card className="p-8 text-center bg-card border-border/50">
              <Megaphone className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground mb-2">No marketing data recorded yet</p>
              <p className="text-sm text-muted-foreground">Track promo performance, QR scans, post-show sales</p>
            </Card>
          )}
        </TabsContent>

        {/* Booth Tab */}
        <TabsContent value="booth" className="mt-4 space-y-4">
          {boothAnalytics && boothAnalytics.total_events_tracked > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
                <StatCard title="Booth Visitors" value={boothAnalytics.total_booth_visitors?.toLocaleString()} />
                <StatCard title="Transactions" value={boothAnalytics.total_transactions} />
                <StatCard title="Avg Stop Rate" value={`${boothAnalytics.avg_stop_rate}%`} />
                <StatCard title="Avg Conversion" value={`${boothAnalytics.avg_conversion_rate}%`} color="text-primary" />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="bg-card border-border/50">
                  <CardHeader><CardTitle className="text-base">Payment Methods</CardTitle></CardHeader>
                  <CardContent>
                    <div className="flex gap-4">
                      <div className="flex-1 p-4 bg-secondary/30 rounded-lg text-center">
                        <p className="text-2xl font-bold text-primary">{boothAnalytics.avg_card_percent}%</p>
                        <p className="text-xs text-muted-foreground">Card/Tap</p>
                      </div>
                      <div className="flex-1 p-4 bg-secondary/30 rounded-lg text-center">
                        <p className="text-2xl font-bold text-green-400">{boothAnalytics.avg_cash_percent}%</p>
                        <p className="text-xs text-muted-foreground">Cash</p>
                      </div>
                    </div>
                    {boothAnalytics.total_failed_transactions > 0 && (
                      <p className="text-sm text-red-400 mt-3">Failed transactions: {boothAnalytics.total_failed_transactions}</p>
                    )}
                  </CardContent>
                </Card>
                <Card className="bg-card border-border/50">
                  <CardHeader><CardTitle className="text-base">Top Converting Events</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {boothAnalytics.event_metrics?.slice(0, 5).map((e) => (
                        <div key={e.event_id} className="flex items-center justify-between p-2 bg-secondary/30 rounded-lg">
                          <span className="text-sm truncate max-w-[150px]">{e.event_name}</span>
                          <span className="font-bold text-primary">{e.conversion_rate}%</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          ) : (
            <Card className="p-8 text-center bg-card border-border/50">
              <Layout className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground mb-2">No booth data recorded yet</p>
              <p className="text-sm text-muted-foreground">Track visitors, conversions, layout effectiveness</p>
            </Card>
          )}
        </TabsContent>

        {/* Growth Tab */}
        <TabsContent value="growth" className="mt-4 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
            <StatCard title="Total Events" value={events.length} />
            <StatCard title="Profitable" value={events.filter(e => (e.total_revenue || 0) - (e.total_expenses || 0) - (e.booth_cost || 0) > 0).length} color="text-green-400" />
            <StatCard title="Avg Revenue" value={formatCurrency(events.length > 0 ? events.reduce((sum, e) => sum + (e.total_revenue || 0), 0) / events.length : 0)} color="text-primary" />
            <StatCard title="Shows Ranked" value={growthAnalytics?.total_shows_ranked || 0} />
          </div>
          <Card className="bg-card border-border/50">
            <CardHeader><CardTitle className="text-base">Revenue by Event</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={revenueByEvent}><CartesianGrid strokeDasharray="3 3" stroke="#27272a" /><XAxis dataKey="name" stroke="#a1a1aa" tick={{ fontSize: 10 }} /><YAxis stroke="#a1a1aa" /><Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }} /><Bar dataKey="revenue" fill="#22c55e" /></BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          {growthAnalytics?.top_ranked_shows?.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="bg-green-500/10 border-green-500/30">
                <CardHeader><CardTitle className="text-base text-green-400">Shows to Repeat</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {growthAnalytics.repeat_recommendations?.map((s) => (
                      <div key={s.event_id} className="flex items-center justify-between p-2 bg-secondary/30 rounded-lg">
                        <span className="text-sm">{s.event_name}</span>
                        <span className="font-bold text-green-400">{s.overall_score}/10</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-red-500/10 border-red-500/30">
                <CardHeader><CardTitle className="text-base text-red-400">Consider Skipping</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {growthAnalytics.skip_recommendations?.map((s) => (
                      <div key={s.event_id} className="flex items-center justify-between p-2 bg-secondary/30 rounded-lg">
                        <span className="text-sm">{s.event_name}</span>
                        <span className="font-bold text-red-400">{s.overall_score}/10</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
